/** tm/components/GovTargetReviewRow.tsx — 「세부업종 · 전용 대상 자동 체크 검토」 한 줄 (2026-10-05r 속도 — 지도사 「여기서 칸을 클릭하니까 한참 대기 상태에 있어」)
 *
 *  pages/GovTargetReview.tsx 에서 떼어 냈다 — 줄마다 React.memo 라 한 줄을 고쳐도 다른 줄은 다시 그리지 않는다.
 *  손놀림(handlers)은 부모가 한 번 만든 고정 객체 — 줄은 id · 키만 넘긴다.
 *  applyReviewAction = 서버 govTargets.reviewAction 의 «화면 줄» 판 — 누르는 즉시 화면에 반영(낙관적 갱신) · 서버 응답이 오면 그 값으로 맞춘다.
 */
import { memo } from 'react';
import { C, T, R, DDayBadge } from './ui';
import type { GovTargetRow, GovTargetSnap, GovAiConf } from '../types';
import { TONE, toneOf, FROM_LABEL, CONF_LABEL, CONF_COLOR, targetText, TECH_KEY } from './GovTargetChips';
import { GOV_SPECIAL_INFO, GOV_SUB_INFO, parseSubKey, isSubKey } from './GovTaxonomy';

export type ReviewStatus = 'pending' | 'done';
export type ReviewBody = { action: 'confirm' | 'none' } | { action: 'exclude' | 'include'; k: string } | { action: 'restore'; snap: GovTargetSnap };
type Item = GovTargetRow['items'][number];

/** 참고 값(AI 가 확인하지 않은 낱말 판정)인가 — 켜져 있지도 빠져 있지도 않다 */
export const isPendingHint = (it: Item) => it.from === 'hint' && !it.on && !it.off;
const uniq = (a: string[]) => a.filter((x, i) => a.indexOf(x) === i);

/**
 * 검토 동작 → 화면 줄 (서버 reviewAction · reviewRow 와 같은 규칙 — 서버 응답이 오면 그 줄로 덮는다)
 *  confirm 맞음 · exclude{k} 그 값만 아님(남은 자동 값이 없으면 검토 끝) · include{k} 켬 · none 모두 아님 · restore{snap} 직전으로
 */
export function applyReviewAction(row: GovTargetRow, body: ReviewBody): GovTargetRow {
  let on = uniq([...(row.subs || []), ...row.sec]);
  let off = uniq(row.sec_off.slice());
  let reviewed = row.reviewed;
  const autoKs = row.items.map(x => x.k);
  if (body.action === 'confirm') reviewed = true;
  else if (body.action === 'exclude') {
    on = on.filter(x => x !== body.k); if (!off.includes(body.k)) off.push(body.k);
    if (!autoKs.some(x => !off.includes(x))) reviewed = true;
  } else if (body.action === 'include') {
    off = off.filter(x => x !== body.k); if (!on.includes(body.k)) on.push(body.k);
  } else if (body.action === 'none') {
    on = on.filter(x => !autoKs.includes(x)); off = uniq(off.concat(autoKs)); reviewed = true;
  } else if (body.action === 'restore') {
    on = uniq([...(body.snap.subs || []), ...body.snap.sec]); off = uniq(body.snap.sec_off.slice()); reviewed = body.snap.reviewed === true;
  }
  on = on.filter(x => !off.includes(x));
  return {
    ...row, subs: on.filter(isSubKey), sec: on.filter(k => !isSubKey(k)), sec_off: off, reviewed,
    items: row.items.map(it => {
      const isOn = on.includes(it.k), isOff = off.includes(it.k);
      if (isOff === it.off && (it.from !== 'hint' || !!it.on === isOn) && !(it.inAny && (isOn || isOff))) return it;
      return { ...it, off: isOff, inAny: isOn || isOff ? false : it.inAny, ...(it.from === 'hint' ? { on: isOn } : {}) };
    }),
  };
}
/** 줄 → 되돌리기 값 */
export const snapOf = (row: GovTargetRow): GovTargetSnap => ({ subs: row.subs || [], sec: row.sec, sec_off: row.sec_off, reviewed: row.reviewed });

/** 근거 문장 — 잡힌 낱말을 형광으로 · AI 판정은 공고문 인용문(「 」) */
function Evidence({ ev, m, quote }: { ev: string; m?: string; quote?: boolean }) {
  if (!ev) return <span style={{ color: C.muted }}>{quote ? '인용한 자격 문장 없음' : '근거 문장 없음'}</span>;
  if (quote) return <span>「{ev}」</span>;
  const i = m ? ev.indexOf(m) : -1;
  if (i < 0) return <span>{ev}</span>;
  return <span>{ev.slice(0, i)}<mark style={{ background: '#FDE68A', color: C.text, padding: '0 2px', borderRadius: 3 }}>{m}</mark>{ev.slice(i + (m || '').length)}</span>;
}
/** AI 확신 표 */
function ConfBadge({ conf }: { conf?: GovAiConf | null }) {
  if (!conf) return null;
  return <span style={{ fontSize: 10.5, fontWeight: 700, color: CONF_COLOR[conf] || C.sub, border: `1px solid ${CONF_COLOR[conf] || C.border}55`, borderRadius: 4, padding: '0 5px', lineHeight: '16px', whiteSpace: 'nowrap' }}>{CONF_LABEL[conf] || conf}</span>;
}
/** 직접 값과 다른 점(-뺌 +켬) → 글 */
const diffText = (d: string[], removed: string[] = []) => d.map(x => (x.startsWith('-') ? `${removed.includes(x.slice(1)) ? '옛 값' : '직접 켠'} ${targetText(x.slice(1))} 은 본문에서 못 찾${removed.includes(x.slice(1)) ? '아 껐음' : '음'}` : x.startsWith('+') ? `「아님」한 ${targetText(x.slice(1))} 이 본문에선 대상` : x)).join(' · ');   // 2026-10-07f 껐음 = AI 를 믿고 실제로 뺀 값(되살리려면 「되살리기」)
const titleHintText = (ks: string[]) => (ks.length ? `AI 가 답하지 않은 사업명 규칙 값(켜 둠 — 본문을 보고 확인): ${ks.map(targetText).join(' · ')}` : '');   // 2026-10-07j 규칙은 AI 에게 묻는 질문 — 답을 빠뜨린 값만 켜 두고 지도사가 본다
/** AI 가 공고에서 새로 나눈 세부 유형(한도 · 대상이 다른 유형 — 2026-10-07g) — 공고 수정 화면의 세부 유형 탭에서 확인 */
const tracksNewText = (names: string[]) => (names.length ? `세부 유형 ${names.length}개 새로 나눔(확인): ${names.join(' · ')}` : '');
const hasAi = (row: GovTargetRow) => !!row.ai && !!(row.ai.conf || row.ai.note || row.ai.diff.length > 0 || (row.ai.titleHint || []).length > 0 || (row.ai.tracksNew || []).length > 0);
const tipOf = (k: string) => { if (k === TECH_KEY) return '본문의 「기술 기반 (예비)창업기업」 — 업종을 직접 정하지 않은 공고는 제조업 · 연구개발업 · IT/SW개발업 업체에만 추천합니다 (2026-10-05l)'; const x = parseSubKey(k); return x ? GOV_SUB_INFO[x.s] : GOV_SPECIAL_INFO[k]?.tip; };
const GROUPS: [string, (k: string) => boolean][] = [['세부업종', (k: string) => !!parseSubKey(k) || k === TECH_KEY], ['전용 대상', (k: string) => !parseSubKey(k) && k !== TECH_KEY]];
const MARK = { background: '#FDE68A', color: C.text, padding: '0 2px', borderRadius: 3 } as const;

/** 줄이 부르는 손놀림 — 부모가 한 번 만든 고정 객체(줄 memo 가 깨지지 않게) */
export interface ReviewHandlers {
  cursor: (id: string) => void;
  open: (id: string) => void;
  confirm: (id: string) => void;
  none: (id: string) => void;
  reopen: (id: string) => void;
  toggle: (id: string, k: string, turnOn: boolean) => void;
  reread: (id: string) => void;
}
interface RowProps { row: GovTargetRow; index: number; cur: boolean; status: ReviewStatus; aiBusy: boolean; h: ReviewHandlers }

function ReviewRowView({ row, index, cur, status, aiBusy, h }: RowProps) {
  const on = row.items.filter(x => !x.off);
  return (
    <div data-row={row.id} className={`gtr-row${cur ? ' cur' : ''}`} onClick={() => h.cursor(row.id)} onDoubleClick={e => { if ((e.target as HTMLElement).closest('button, a, input, kbd')) return; h.open(row.id); }} title="두 번 누르면 등록된 공고에서 열기" aria-label={`${index + 1}번째 공고 ${row.name}`}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <button type="button" className="gtr-name" onClick={e => { e.stopPropagation(); h.open(row.id); }} title="등록된 공고에서 열기 (Enter)"
            style={{ minWidth: 0, border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', fontSize: T.md, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</button>
          {!row.first_come && row.deadline && <span style={{ flexShrink: 0 }}><DDayBadge deadline={row.deadline} /></span>}
        </div>
        <div style={{ fontSize: T.xs, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {[row.agency, row.first_come ? '선착순' : row.deadline ? `마감 ${row.deadline.slice(0, 10)}` : '상시', row.report_name && row.report_name !== row.name ? row.report_name : ''].filter(Boolean).join(' · ')}
        </div>
        {/* 등록된 업종 (2026-10-05s 지도사 「수정에서 제조업으로 맞게 해놨는데 첫 화면에는 왜 안 나와」) — 세부업종 칩 위에 늘 보인다 · 바꾸려면 공고 열기 */}
        <div className="gtr-grp">
          <span className="gl">업종</span>
          {(row.biz || []).length
            ? (row.biz || []).map(b => <span key={b} style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.bgAlt, color: C.text, fontSize: T.sm, fontWeight: 600 }}>{b}</span>)
            : <span style={{ fontSize: T.sm, color: C.muted }}>무관</span>}
        </div>
        {GROUPS.map(([title, test]) => {
          // 자동 체크 기록에 없는 «직접 고른» 값도 보인다 (2026-10-05s 지도사 「왜 하나만 나오냐 — 실제 들어가 보면 클릭된 게 상당히 많은데」) · 번호(1~9 단축키)는 자동 값만
          const autoKs = row.items.map(x => x.k);
          const manual = [...(row.subs || []), ...(row.sec || [])].filter(k => autoKs.indexOf(k) < 0)
            .map((k): GovTargetRow['items'][number] & { manual: boolean } => ({ k, from: 'old', ev: '', off: false, strict: false, label: targetText(k), manual: true }));
          const list = [...row.items.map((it, i) => ({ it: { ...it, manual: false }, i })), ...manual.map(it => ({ it, i: 99 }))].filter(x => test(x.it.k));
          if (!list.length) return null;
          return (
            <div key={title} className="gtr-grp">
              <span className="gl">{title}</span>
              {list.map(({ it, i }) => {
                const t = TONE[toneOf(it.k)];
                const label = it.label || targetText(it.k);
                const pending = isPendingHint(it);   // 참고 — 켜져 있지 않다
                const dim = it.off || pending;
                return (
                  <span key={it.k} className={`gtr-chip${pending ? ' hint' : ''}`} title={pending ? `낱말로만 잡힌 값 — AI 판정이 확인하지 않아 켜지 않았습니다. 누르면 켭니다\n\n${tipOf(it.k) || ''}` : tipOf(it.k)}
                    style={{ border: `1px ${dim || it.inAny ? 'dashed' : 'solid'} ${it.off ? C.borderDk : t.bd}`, background: dim ? C.white : t.bg, color: it.off ? C.muted : t.fg, fontWeight: dim ? 400 : 600, paddingRight: it.inAny ? 9 : undefined }}>
                    {i < 9 && !it.inAny && <span className="n">{i + 1}</span>}
                    <span style={{ textDecoration: it.off ? 'line-through' : 'none' }}>{label}</span>
                    {pending && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.8 }}>참고</span>}
                    {it.manual && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.6 }} title="공고 수정에서 직접 고른 값">직접</span>}
                    {it.strict && !dim && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>확인 필요</span>}
                    {it.outside && !dim && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }} title="고른 업종 밑에 없는 분야라 「무관 › …」으로 켰습니다 — 고른 업종 업체 가운데 이 분야 업체에만 추천됩니다(제조업 + 스포츠 = 스포츠용품 제조). 맞는지 확인해 주세요">{it.outsideLabel || '업종 안 그 분야만 — 확인'}</span>}
                    {it.inAny && <span style={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }} title="옛 「전용 분야 또는 기업 유형」을 「또는」 조건으로 옮겼습니다 — 공고를 열어 고칠 수 있습니다">또는 조건</span>}
                    {!it.inAny && (pending ? (
                      <button type="button" className="go" aria-label={`${label} 켜기`} title="이 값을 켭니다(매칭에 적용)"
                        onClick={e => { e.stopPropagation(); h.cursor(row.id); h.toggle(row.id, it.k, true); }}>켜기</button>
                    ) : (
                      <button type="button" className="x" aria-label={it.off ? `${label} 다시 켜기` : `${label} 빼기`} title={it.off ? '다시 켜기' : '아님 — 빼기'}
                        onClick={e => { e.stopPropagation(); h.cursor(row.id); h.toggle(row.id, it.k, it.off); }}>{it.off ? '↺' : '✕'}</button>
                    ))}
                  </span>
                );
              })}
            </div>
          );
        })}
        {row.ai && hasAi(row) && (
          <div className="gtr-ai">
            <span style={{ fontWeight: 700, color: C.sub }}>AI 판정</span>
            <ConfBadge conf={row.ai.conf} />
            {row.ai.note && <span>{row.ai.note}</span>}
            {row.ai.diff.length > 0 && <span style={{ color: '#92400E' }}>{diffText(row.ai.diff, row.ai.removed || [])}</span>}
            {(row.ai.titleHint || []).length > 0 && <span style={{ color: '#1D4ED8' }}>{titleHintText(row.ai.titleHint || [])}</span>}
            {(row.ai.tracksNew || []).length > 0 && <span style={{ color: '#047857' }}>{tracksNewText(row.ai.tracksNew || [])}</span>}
            {row.ai.biz.length > 0 && !(row.biz || []).length && <span style={{ color: C.muted }}>AI 가 읽은 업종: {row.ai.biz.join(' · ')} (등록 업종 없음 — 참고)</span>}
            {row.ai.at && <span style={{ color: C.muted, marginLeft: 'auto' }}>{row.ai.at.slice(0, 10)}</span>}
          </div>
        )}
        {(row.req || []).length > 0 && (
          <div className="gtr-ai" aria-label="필수 자격">
            <span style={{ fontWeight: 700, color: C.sub }}>필수 자격</span>
            {(row.req || []).map(q => (
              <span key={q.t} title={q.ev ? `공고: ${q.ev}` : undefined}
                style={{ padding: '1px 8px', borderRadius: 999, border: `1px solid ${q.k === 'gain' ? '#FCD34D' : C.border}`, background: q.k === 'gain' ? '#FFFBEB' : C.white, color: C.text, fontSize: T.xs, fontWeight: 600 }}>
                {q.t}{q.k === 'gain' ? ' · 신청 전 갖추면 됨' : ''}{q.f.length ? ` (= ${q.f.join(' · ')})` : ''}
              </span>
            ))}
          </div>
        )}
        <div className="gtr-ev">
          {row.items.map(it => {
            const pending = isPendingHint(it);
            const src = it.from === 'hint' ? (it.src || 'body') : it.from;
            return (
              <div key={it.k} className="gtr-evl" style={{ color: it.off || pending ? C.muted : C.sub }}>
                <span className="gtr-from">{it.from === 'hint' ? `참고 · ${FROM_LABEL[src] || src}` : FROM_LABEL[it.from] || it.from}</span>
                {row.items.length > 1 && <b style={{ fontWeight: 600, color: it.off || pending ? C.muted : TONE[toneOf(it.k)].fg, textDecoration: it.off ? 'line-through' : 'none', marginRight: 6 }}>{it.label || targetText(it.k)}</b>}
                {it.from === 'ai' && it.conf && <span style={{ marginRight: 6 }}><ConfBadge conf={it.conf} /></span>}
                {src === 'title'
                  ? <span>사업명의 <mark style={MARK}>{it.m || it.k}</mark></span>
                  : src === 'old'
                    ? <span>예전에 고른 분류 <mark style={MARK}>{it.m || it.ev}</mark> 를 새 분류로 옮김</span>
                    : <Evidence ev={it.ev} m={it.m} quote={it.from === 'ai'} />}
              </div>
            );
          })}
        </div>
        {status === 'done' && (row.by || on.length === 0) && (
          <div style={{ fontSize: T.xs, color: C.muted, marginTop: 6 }}>{on.length ? `켜짐: ${on.map(x => x.label || targetText(x.k)).join(' · ')}` : '자동 체크 없음 — 업종 조건대로 추천'}{row.by ? ` · ${row.by}` : ''}</div>
        )}
      </div>
      <div className="gtr-acts" onClick={e => e.stopPropagation()}>
        {status === 'pending' ? (
          <>
            <button type="button" className="gtr-btn" onClick={() => { h.cursor(row.id); h.confirm(row.id); }}
              style={{ border: `1px solid ${C.green}66`, background: C.greenLt, color: C.green }} title="켠 값이 맞음 (Y)">맞음 <kbd>Y</kbd></button>
            <button type="button" className="gtr-btn" onClick={() => { h.cursor(row.id); h.none(row.id); }}
              style={{ border: `1px solid ${C.border}`, background: C.white, color: C.sub }} title="켠 값이 모두 아님 — 빼고 업종 조건대로 추천 (X)">모두 아님 <kbd>X</kbd></button>
          </>
        ) : (
          <button type="button" className="gtr-btn" onClick={() => { h.cursor(row.id); h.reopen(row.id); }}
            style={{ border: `1px solid ${C.border}`, background: C.white, color: C.sub }} title="검토 대기로 되돌리기 (R)">다시 검토 <kbd>R</kbd></button>
        )}
        <button type="button" className="gtr-btn" disabled={aiBusy} onClick={() => { h.cursor(row.id); h.reread(row.id); }}
          style={{ border: `1px solid ${C.accent}55`, background: C.accentLt, color: C.accent, opacity: aiBusy ? 0.6 : 1 }} title="공고문 전체를 AI 가 다시 읽고 세부업종 · 전용 대상을 다시 판정합니다 (A)">{aiBusy ? '읽는 중…' : <>AI 다시 읽기 <kbd>A</kbd></>}</button>
      </div>
    </div>
  );
}
/** 줄 — row 객체 · 커서 · AI 읽는 중이 바뀐 줄만 다시 그린다 */
export const ReviewRow = memo(ReviewRowView);

/** 그리기 전 높이 어림(px · 줄 사이 8 포함) — 화면에 들어오면 실제 높이로 바꾼다 */
export function estimateRowHeight(row: GovTargetRow): number {
  const groups = GROUPS.filter(([, test]) => row.items.some(it => test(it.k))).length;
  const ai = hasAi(row) ? 38 : 0;
  return 24 + 44 + groups * 32 + ai + 8 + row.items.length * 21 + 8;
}
